import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/core/services/auth.service';
import {
  ShareLegalInformationConfirmationDialogComponent,
  LegalInformationLawyer
} from '../share-legal-information-confirmation-dialog/share-legal-information-confirmation-dialog.component';

interface Lawyer extends LegalInformationLawyer {
  avatar?: string;
  selected: boolean;
  /** Whether this pre-read is ALREADY shared with this lawyer, per the API's own is_shared flag */
  isShared: boolean;
  /** From the API's isSubActive flag; only an explicit false means the lawyer's subscription expired */
  isSubActive?: boolean;
}

export interface ShareLegalInformationDocument {
  /** The pre_read_id — used for every API call in this dialog. Never hard-code this. */
  id: string;
  title?: string;
  name?: string;
  description?: string;
  issue_type?: string[];
  status?: string;
  updated_at?: string;
}

export interface ShareLegalInformationDialogData {
  document: ShareLegalInformationDocument;
}

@Component({
  selector: 'app-share-legal-information-dialog',
  templateUrl: './share-legal-information-dialog.component.html',
  styleUrls: ['./share-legal-information-dialog.component.css'],
  standalone: false
})
export class ShareLegalInformationDialogComponent implements OnInit {

  lawyers: Lawyer[] = [];
  filteredLawyers: Lawyer[] = [];
  searchQuery = '';

  loading = false;
  loadError = false;

  /**
   * True once at least one share or unshare actually went through during
   * this dialog session. Unsharing alone doesn't go through finishShare()
   * (that only runs from the "Next" > new-shares flow), so without this
   * flag, closing via Cancel/the X after only unsharing reports no result
   * at all — the caller never learns anything changed, and the lawyer's
   * name keeps showing in the underlying view until a full page reload.
   */
  private changesMade = false;

  constructor(
    private dialogRef: MatDialogRef<ShareLegalInformationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShareLegalInformationDialogData,
    private auth: AuthService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadLawyers();
  }

  /**
   * GET /consumer/v3/prereads/:pre_read_id/shared-with, merged with the
   * client's full relationship list so lawyers who exist as a relationship
   * but have never been shared with (is_shared: false) still show up —
   * the shared-with endpoint returns every candidate, not just actual
   * shares, so BOTH sources are unioned rather than only trusting one.
   */
  loadLawyers(): void {
    const preReadId = this.data?.document?.id;
    if (!preReadId) {
      this.loadError = true;
      return;
    }

    this.loading = true;
    this.loadError = false;

    let relationshipsFailed = false;
    let sharedFailed = false;

    forkJoin({
      relationships: this.auth.getPreReadSharedWith(preReadId).pipe(
        catchError(() => { relationshipsFailed = true; return of({}); })
      ),
      shared: this.auth.getPreReadSharedWith(preReadId).pipe(
        catchError(() => { sharedFailed = true; return of({}); })
      )
    }).pipe(
      finalize(() => this.loading = false)
    ).subscribe(({ relationships, shared }: any) => {
      // Only treat this as a hard failure if BOTH calls actually failed —
      // either source alone is still useful data, and neither being empty
      // by itself means "no lawyers available".
      if (relationshipsFailed && sharedFailed) {
        this.loadError = true;
        this.lawyers = [];
        this.filteredLawyers = [];
        return;
      }

      const sharedItems = this.extractLawyers(shared);
      const sharedById = new Map(sharedItems.map(l => [l.id, l]));
      const relationshipItems = this.extractLawyers(relationships);
      const relationshipIds = new Set(relationshipItems.map(l => l.id));
      const extraFromShared = sharedItems.filter(l => !relationshipIds.has(l.id));

      this.lawyers = [...relationshipItems, ...extraFromShared].map(l => {
        const isShared = sharedById.get(l.id)?.isShared === true;
        return { ...l, selected: isShared, isShared, isSubActive: l.isSubActive ?? sharedById.get(l.id)?.isSubActive };
      });
      this.filterLawyers(this.searchQuery);
    });
  }

  /**
   * Un-nests either the shared-with response ({ error, pre_read: { shared_with: [...] } })
   * or the client relationships response into a flat Lawyer[]. The
   * relationship_id (never an array index, name, or generated id) becomes
   * this row's `id` and is what every share/unshare API call is keyed on.
   */
  private extractLawyers(response: any): Lawyer[] {
    const body = response?.data ?? response;
    const container = body?.pre_read ?? body?.preRead ?? body;
    const raw = container?.shared_with ?? container?.sharedWith ?? container?.lawyers ?? container?.relationships ?? body;
    const values = Array.isArray(raw) ? raw : raw ? [raw] : [];

    return values.map((item: any) => {
      const person = item?.lawyer ?? item?.user ?? item?.profile ?? item;
      const isShared = item?.is_shared === true || item?.isShared === true;
      return {
        id: String(item?.relationship_id ?? item?.relId ?? item?.relationshipId ?? item?.rel_id ?? person?.id ?? ''),
        name: person?.name ?? item?.name ?? person?.full_name ?? [person?.first_name, person?.last_name].filter(Boolean).join(' '),
        email: person?.email ?? item?.email ?? '',
        avatar: person?.avatar ?? person?.profileUrl ?? person?.profile_url,
        firmName: person?.firmName ?? person?.firm_name ?? item?.firm_name ?? person?.firm ?? person?.company ?? person?.organization ?? '',
        selected: isShared,
        isShared,
        isSubActive: item?.isSubActive ?? person?.isSubActive
      } as Lawyer;
    }).filter((lawyer: Lawyer) => lawyer.id && (lawyer.name || lawyer.email));
  }

  onSearchChange(value: string): void {
    this.searchQuery = value;
    this.filterLawyers(value);
  }

  private filterLawyers(query: string): void {
    const q = (query || '').trim().toLowerCase();
    this.filteredLawyers = q
      ? this.lawyers.filter(l =>
          (l.name || '').toLowerCase().includes(q) ||
          (l.firmName || '').toLowerCase().includes(q))
      : [...this.lawyers];
  }

  get selectedLawyers(): Lawyer[] {
    return this.lawyers.filter(l => l.selected);
  }

  get hasSelection(): boolean {
    return this.selectedLawyers.length > 0;
  }

  /**
   * Unchecking a lawyer who is ALREADY shared (is_shared === true) is a
   * revoke action and goes through the same confirmation dialog as sharing
   * — the checkbox itself doesn't flip until the user confirms. Selecting
   * (or deselecting a not-yet-shared lawyer) toggles immediately.
   */
  isInactive(lawyer: Lawyer): boolean {
    return lawyer.isSubActive === false;
  }

  inactiveTip(lawyer: Lawyer): string {
    return `Sharing legal information with Lawyer ${lawyer.name} is currently disabled. Please try again after some time.`;
  }

  toggleSelection(lawyer: Lawyer): void {
    if (this.isInactive(lawyer)) return;
    if (lawyer.selected && lawyer.isShared) {
      this.showUnshareConfirmation(lawyer);
    } else {
      lawyer.selected = !lawyer.selected;
    }
  }

  private showUnshareConfirmation(lawyer: Lawyer): void {
    const ref = this.dialog.open(ShareLegalInformationConfirmationDialogComponent, {
      data: {
        document: this.data.document,
        selectedLawyers: [lawyer],
        action: 'unshare'
      },
      width: '500px',
      disableClose: true
    });

    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.unshareLawyer(lawyer);
        return;
      }
      // Cancelled — lawyer.selected/isShared are left untouched, since
      // nothing actually changed. But mat-checkbox flips its own visual
      // state the instant it's clicked, and since the underlying value
      // here never changes, Angular's change detection sees the same
      // bound value on the next check and skips re-pushing `checked`
      // down — leaving the box visually unchecked even though the data
      // still says shared. Force a real value change across a genuine
      // change-detection boundary so the checkbox re-syncs to checked.
      lawyer.selected = false;
      this.cdr.detectChanges();
      lawyer.selected = true;
    });
  }

  /** DELETE /consumer/v3/relationships/:relid/prereads/unshare */
  private unshareLawyer(lawyer: Lawyer): void {
    const preReadId = this.data.document.id;
    this.auth.unsharePreRead(lawyer.id, preReadId).subscribe({
      next: () => {
        lawyer.selected = false;
        lawyer.isShared = false;
        this.changesMade = true;
        this.toastr.success(`Legal information unshared with ${lawyer.name}`, 'Success');
      },
      error: () => this.toastr.error(`Failed to unshare legal information with ${lawyer.name}`, 'Error')
    });
  }

  onCancel(): void {
    // If nothing changed this session, close exactly as before (no result).
    // If an unshare went through, the caller still needs to know to refresh
    // its own display — even though the user left via Cancel/X rather than
    // "Next" — so it doesn't keep showing a lawyer who was just removed.
    this.dialogRef.close(this.changesMade ? { refresh: true, shared: false } : undefined);
  }

  /**
   * Next only opens the confirmation dialog — the share API is never called
   * from here. Only lawyers who aren't already shared go to confirmation;
   * re-confirming an existing share is a no-op the user didn't ask for.
   */
  onNext(): void {
    if (!this.hasSelection) { return; }

    const newShares = this.selectedLawyers.filter(l => !l.isShared);
    if (!newShares.length) {
      this.toastr.info('All selected lawyers already have access to this legal information.', 'Info');
      return;
    }

    const ref = this.dialog.open(ShareLegalInformationConfirmationDialogComponent, {
      data: {
        document: this.data.document,
        selectedLawyers: newShares,
        action: 'share'
      },
      width: '500px',
      disableClose: true
    });

    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.shareWithLawyers(newShares);
      }
      // Cancelled — stay open on the picker so the user can adjust selection.
    });
  }

  /**
   * POST /consumer/v3/relationships/:relid/prereads/share, body { pre_read_id }
   * — one request per selected lawyer (the API is keyed on relationship,
   * not batched), with partial failures reported rather than swallowed.
   */
  private shareWithLawyers(selected: Lawyer[]): void {
    const preReadId = this.data.document.id;
    let completed = 0;
    let failed = 0;
    const total = selected.length;

    selected.forEach(lawyer => {
      this.auth.sharePreRead(lawyer.id, preReadId).subscribe({
        next: () => {
          completed++;
          lawyer.isShared = true;
          lawyer.selected = true;
          if (completed + failed === total) { this.finishShare(completed, failed, total); }
        },
        error: () => {
          failed++;
          if (completed + failed === total) { this.finishShare(completed, failed, total); }
        }
      });
    });
  }

  private finishShare(successful: number, failed: number, total: number): void {
    if (successful === total) {
      this.toastr.success('Legal information shared successfully.', 'Success');
    } else if (successful > 0) {
      this.toastr.warning(`Legal information shared with ${successful} out of ${total} recipients`, 'Partial Success');
    } else {
      this.toastr.error('Failed to share legal information with any recipients', 'Error');
    }
    // Only report success (shared: true) when at least one share actually
    // went through, so callers don't show a success toast on a fully-failed
    // attempt. refresh is still true whenever changesMade (e.g. an unshare
    // earlier in this same session) even if this particular share attempt
    // failed entirely.
    this.dialogRef.close({ refresh: successful > 0 || this.changesMade, shared: successful > 0 });
  }

  getInitial(name: string): string {
    return (name || '').charAt(0).toUpperCase();
  }

  getAvatarColor(id: string): string {
    const colors = ['#0071C5'];
    const index = parseInt(id, 10) % colors.length;
    return colors[isNaN(index) ? 0 : index];
  }

  hasValidAvatar(lawyer: Lawyer): boolean {
    return !!(lawyer.avatar && lawyer.avatar.trim() !== '');
  }
}